import {Component, OnInit, Input, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs/operators';
import {
    AccountBalance,
    FinanceAccount,
    FinanceApiService,
    FinanceBucket,
    FinanceException,
    FinancePayment,
    FinanceSummary,
    ImportResult,
    PayoutView
} from '../api/finance-api.service';
import {downloadCsv} from '../shared/csv.util';
import {ChapterApiService} from '../api/chapter-api.service';
import {escapeHtml, ranaDtOptions, RanaDtOptions} from '../shared/rana-datatable-defaults';

type Tab = 'overview' | 'payments' | 'reconciliation' | 'payouts' | 'accounts';

/**
 * NEC finance dashboard. NEC roles see every chapter and can refund / import / manage Stripe accounts; chapter
 * leaders get a read-only view of their own chapter (enforced by the API, hidden here only for tidiness).
 */
@Component({
    selector: 'app-finance',
    templateUrl: './finance.component.html',
    styleUrl: './finance.component.css'
})
export class FinanceComponent implements OnInit {

    readonly userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
    readonly canManage: boolean = !!this.userRole?.FINANCE?.includes('APPROVE');

    tab: Tab = 'overview';
    loading = false;

    // NEC can look at any one chapter (or everything); chapter leaders are pinned to their own by the server.
    chapterId = '';
    /** True when shown inside a chapter workspace (keeps the sidebar) rather than as its own page. */
    @Input() embedded = false;
    /** Chapter to start on when embedded (NEC only; empty = all chapters). */
    @Input() initialChapter = '';
    chapterOptions: { key: string; label: string }[] = [];
    private chapterNames: { [id: string]: string } = {};

    // overview
    from = '';
    to = '';
    summary: FinanceSummary | null = null;
    balances: AccountBalance[] | null = null;
    balancesLoading = false;

    // payments
    // The four list tables are DataTables like the rest of the app (search, sort, paging). Whenever the data
    // is replaced the table is rebuilt: a flag toggled off/on re-creates the <table> with fresh options.
    private _payments: FinancePayment[] = [];
    get payments(): FinancePayment[] { return this._payments; }
    set payments(rows: FinancePayment[]) { this._payments = rows; this.rebuildTable('payments'); }
    paymentsDt: RanaDtOptions = ranaDtOptions({columns: []});
    paymentsDtShow = false;
    filters = {status: '', purpose: '', q: '', from: '', to: ''};
    page = 1;
    selected: FinancePayment | null = null;

    // refund panel
    refundOpen = false;
    refundAmount = '';
    refundReason = '';
    refundBusy = false;

    // reconciliation
    private _exceptions: FinanceException[] = [];
    get exceptions(): FinanceException[] { return this._exceptions; }
    set exceptions(rows: FinanceException[]) { this._exceptions = rows; this.rebuildTable('exceptions'); }
    exceptionsDt: RanaDtOptions = ranaDtOptions({columns: []});
    exceptionsDtShow = false;
    importing = false;
    importLog = '';
    sweeping = false;
    linkTarget: FinancePayment | null = null;
    link = {purpose: 'EVENT_LOCAL', eventId: '', bookingId: '', userId: '', businessConferenceId: ''};

    // payouts / accounts
    private _payouts: PayoutView[] | null = null;
    get payouts(): PayoutView[] | null { return this._payouts; }
    set payouts(rows: PayoutView[] | null) { this._payouts = rows; this.rebuildTable('payouts'); }
    payoutsDt: RanaDtOptions = ranaDtOptions({columns: []});
    payoutsDtShow = false;

    private _accounts: FinanceAccount[] = [];
    get accounts(): FinanceAccount[] { return this._accounts; }
    set accounts(rows: FinanceAccount[]) { this._accounts = rows; this.rebuildTable('accounts'); }
    accountsDt: RanaDtOptions = ranaDtOptions({columns: []});
    accountsDtShow = false;

    @ViewChild('exceptionActionsTpl') exceptionActionsTpl?: TemplateRef<any>;
    @ViewChild('accountSecretTpl') accountSecretTpl?: TemplateRef<any>;
    webhookSecrets: { [ref: string]: string } = {};

    readonly statuses = ['CREATED', 'PROCESSING', 'PAID', 'PARTIALLY_REFUNDED', 'REFUNDED', 'DISPUTED', 'EXPIRED', 'FAILED', 'CANCELLED'];
    readonly purposes = [
        {value: 'MEMBERSHIP', label: 'Membership'},
        {value: 'EVENT_LOCAL', label: 'Event registration'},
        {value: 'EVENT_BOOKING', label: 'Conference booking'},
        {value: 'BIZ_CONFERENCE', label: 'Business conference'},
        {value: 'UNKNOWN', label: 'Not linked'}
    ];

    constructor(private api: FinanceApiService, private toastr: ToastrService, private chapterApi: ChapterApiService,
                private route?: ActivatedRoute) {
        // Opened from a chapter workspace: start on that chapter (NEC only; the server pins chapter leaders anyway).
        const requested = this.route?.snapshot?.queryParamMap?.get('chapter');
        if (requested && this.canManage) this.chapterId = requested;
    }

    ngOnInit(): void {
        if (this.initialChapter && this.canManage) this.chapterId = this.initialChapter;
        if (this.canManage) {
            this.chapterApi.getData().subscribe({
                next: (list: any[]) => {
                    (list || []).forEach(c => this.chapterNames[String(c.id)] = c.name);
                    this.buildChapterOptions();
                },
                error: () => { /* names are cosmetic: ids are shown instead */ }
            });
        }
        this.loadSummary();
        this.loadPayments();
        this.loadExceptions();
    }

    /** Re-load every tab for the chosen chapter (NEC only; empty = all chapters). */
    onChapterChange(): void {
        this.selected = null;
        this.loadSummary();
        this.loadPayments();
        this.loadExceptions();
    }

    private buildChapterOptions(): void {
        if (!this.canManage) return; // chapter leaders are pinned to their own chapter by the server
        // Chapters that actually have payments, plus national (membership / NEC-level) payments.
        const keys = (this.chapterKeys.length ? this.chapterKeys : []).filter(k => k);
        this.chapterOptions = keys.map(k => ({
            key: k,
            label: k === 'NEC' ? 'NEC / membership' : (this.chapterNames[k] || 'Chapter ' + k)
        })).sort((a, b) => a.label.localeCompare(b.label));
    }

    private chapterKeys: string[] = [];

    // ------------------------------------------------------------ navigation

    setTab(tab: Tab): void {
        this.tab = tab;
        if (tab === 'payouts' && this.payouts === null && this.canManage) this.loadPayouts();
        if (tab === 'accounts' && this.accounts.length === 0 && this.canManage) this.loadAccounts();
    }

    // ------------------------------------------------------------ overview

    loadSummary(): void {
        this.loading = true;
        this.api.summary(this.from || undefined, this.to || undefined, this.chapterId || undefined)
            .pipe(finalize(() => this.loading = false))
            .subscribe({
                next: (s) => {
                    this.summary = s;
                    if (!this.chapterId) {
                        // The unfiltered summary lists every chapter that has payments: use it for the selector.
                        this.chapterKeys = s.byChapter.map(b => b.key);
                        this.buildChapterOptions();
                    }
                },
                error: (err) => this.fail('Could not load the finance summary', err)
            });
    }

    loadBalances(): void {
        this.balancesLoading = true;
        this.api.balances().pipe(finalize(() => this.balancesLoading = false)).subscribe({
            next: (b) => this.balances = b,
            error: (err) => this.fail('Could not read the Stripe balances', err)
        });
    }

    get attentionTotal(): number {
        return Object.values(this.summary?.attention || {}).reduce((a, b) => a + b, 0);
    }

    barWidth(bucket: FinanceBucket, all: FinanceBucket[]): number {
        const max = Math.max(...all.map(b => b.gross), 1);
        return Math.round((bucket.gross / max) * 100);
    }

    // ------------------------------------------------------------ payments

    loadPayments(): void {
        this.api.payments({
            status: this.filters.status || undefined,
            purpose: this.filters.purpose || undefined,
            chapterId: this.chapterId || undefined,
            q: this.filters.q || undefined,
            from: this.filters.from || undefined,
            to: this.filters.to || undefined
        }).subscribe({
            next: (rows) => {
                this.payments = rows;
                this.page = 1;
                if (this.selected) this.selected = rows.find(r => r.id === this.selected!.id) || null;
            },
            error: (err) => this.fail('Could not load payments', err)
        });
    }

    select(p: FinancePayment): void {
        this.selected = this.selected?.id === p.id ? null : p;
        this.refundOpen = false;
    }

    refreshFromStripe(p: FinancePayment): void {
        this.loading = true;
        this.api.syncPayment(p.id).pipe(finalize(() => this.loading = false)).subscribe({
            next: (updated) => {
                this.replace(updated);
                this.toastr.success('Refreshed from Stripe');
                this.loadSummary();
                this.loadExceptions();
            },
            error: (err) => this.fail('Could not refresh from Stripe', err)
        });
    }

    exportPayments(): void {
        downloadCsv('rana-payments.csv',
            ['Date', 'Payer email', 'What', 'Event', 'Status', 'Amount', 'Stripe fee', 'Net', 'Refunded', 'Payout', 'Payout status', 'Stripe account', 'PaymentIntent'],
            this.payments.map(p => [
                p.createdDate || '', p.customerEmail || '', this.purposeLabel(p.purpose), p.eventTitle || '',
                p.paymentStatus || '', this.dollars(p.amount), this.dollars(p.stripeFee), this.dollars(this.net(p)),
                this.dollars(p.amountRefunded), p.payoutId || '', p.payoutStatus || '', p.stripeAccountRef || '', p.paymentIntentId || ''
            ]));
    }

    // ------------------------------------------------------------ refunds (NEC)

    openRefund(p: FinancePayment): void {
        this.selected = p;
        this.refundOpen = true;
        this.refundAmount = (this.refundable(p) / 100).toFixed(2);
        this.refundReason = '';
    }

    refundable(p: FinancePayment): number {
        return Math.max(0, (p.amount || 0) - (p.amountRefunded || 0));
    }

    canRefund(p: FinancePayment): boolean {
        return this.canManage && this.isPaid(p) && p.paymentStatus !== 'REFUNDED' && p.paymentStatus !== 'DISPUTED'
            && this.refundable(p) > 0 && !!p.paymentIntentId;
    }

    submitRefund(): void {
        const p = this.selected;
        if (!p) return;
        const dollarsValue = Number(this.refundAmount);
        if (!Number.isFinite(dollarsValue) || dollarsValue <= 0) {
            this.toastr.warning('Enter the amount to refund');
            return;
        }
        const cents = Math.round(dollarsValue * 100);
        if (cents > this.refundable(p)) {
            this.toastr.warning('That is more than the remaining ' + this.money(this.refundable(p)));
            return;
        }
        if (this.refundReason.trim().length < 3) {
            this.toastr.warning('Please give a reason for the refund');
            return;
        }
        this.refundBusy = true;
        this.api.refund(p.id, cents, this.refundReason.trim()).pipe(finalize(() => this.refundBusy = false)).subscribe({
            next: (updated) => {
                this.replace(updated);
                this.refundOpen = false;
                this.toastr.success('Refund of ' + this.money(cents) + ' issued');
                this.loadSummary();
                this.loadExceptions();
            },
            error: (err) => this.fail('Refund failed', err)
        });
    }

    // ------------------------------------------------------------ reconciliation

    loadExceptions(): void {
        this.api.exceptions(this.chapterId || undefined).subscribe({
            next: (list) => this.exceptions = list,
            error: (err) => this.fail('Could not load reconciliation items', err)
        });
    }

    runSweep(): void {
        this.sweeping = true;
        this.api.sweep().pipe(finalize(() => this.sweeping = false)).subscribe({
            next: (r) => {
                this.toastr.success(`Checked ${r['checked']} payments: ${r['paid']} paid, ${r['expired']} expired, ${r['stillOpen']} still open`);
                this.loadPayments();
                this.loadSummary();
                this.loadExceptions();
            },
            error: (err) => this.fail('Sync failed', err)
        });
    }

    startImport(): void {
        this.importing = true;
        this.importLog = 'Importing the last 12 months from Stripe…';
        this.importBatch(null, {imported: 0, updated: 0, skipped: 0, errors: 0});
    }

    private importBatch(before: number | null, total: { imported: number; updated: number; skipped: number; errors: number }): void {
        this.api.importHistory(12, before).subscribe({
            next: (r: ImportResult) => {
                total.imported += r.imported;
                total.updated += r.updated;
                total.skipped += r.skipped;
                total.errors += r.errors;
                this.importLog = `Imported ${total.imported}, updated ${total.updated}, already known ${total.skipped}` +
                    (total.errors ? `, ${total.errors} errors` : '') + (r.done ? ' — finished.' : ' — continuing…');
                if (r.done || r.nextBefore === null) {
                    this.importing = false;
                    this.loadPayments();
                    this.loadSummary();
                    this.loadExceptions();
                } else {
                    this.importBatch(r.nextBefore, total);
                }
            },
            error: (err) => {
                this.importing = false;
                this.fail('Import stopped', err);
            }
        });
    }

    openLink(p: FinancePayment): void {
        this.linkTarget = p;
        this.link = {purpose: 'EVENT_LOCAL', eventId: p.eventId || '', bookingId: p.bookingId || '', userId: p.userId || '', businessConferenceId: ''};
    }

    submitLink(): void {
        if (!this.linkTarget) return;
        const body: any = {purpose: this.link.purpose};
        if (this.link.purpose === 'MEMBERSHIP') body.userId = this.link.userId.trim();
        else if (this.link.purpose === 'BIZ_CONFERENCE') body.businessConferenceId = this.link.businessConferenceId.trim();
        else {
            body.eventId = this.link.eventId.trim();
            body.bookingId = this.link.bookingId.trim();
        }
        this.api.link(this.linkTarget.id, body).subscribe({
            next: (updated) => {
                this.replace(updated);
                this.linkTarget = null;
                this.toastr.success('Payment linked and the registration updated');
                this.loadExceptions();
                this.loadSummary();
            },
            error: (err) => this.fail('Could not link the payment', err)
        });
    }

    markReviewed(p: FinancePayment): void {
        this.api.resolveReview(p.id).subscribe({
            next: (updated) => {
                this.replace(updated);
                this.loadExceptions();
                this.loadSummary();
            },
            error: (err) => this.fail('Could not update the payment', err)
        });
    }

    exceptionsOf(category: string): FinanceException[] {
        return this.exceptions.filter(e => e.category === category);
    }

    // ------------------------------------------------------------ payouts / accounts

    loadPayouts(): void {
        this.loading = true;
        this.api.payouts(90).pipe(finalize(() => this.loading = false)).subscribe({
            next: (p) => {
                this.payouts = p;
                this.loadPayments(); // payments now carry their payout
            },
            error: (err) => this.fail('Could not load payouts', err)
        });
    }

    loadAccounts(): void {
        this.api.accounts().subscribe({
            next: (a) => this.accounts = a,
            error: (err) => this.fail('Could not load Stripe accounts', err)
        });
    }

    saveSecret(a: FinanceAccount): void {
        const secret = (this.webhookSecrets[a.ref] || '').trim();
        this.api.saveWebhookSecret(a.ref, secret).subscribe({
            next: () => {
                this.toastr.success(secret ? 'Webhook secret saved' : 'Webhook secret removed');
                this.webhookSecrets[a.ref] = '';
                this.loadAccounts();
            },
            error: (err) => this.fail('Could not save the webhook secret', err)
        });
    }

    webhookUrl(a: FinanceAccount): string {
        const base = (location.origin.includes('localhost') ? 'http://localhost:8080' : 'https://<your-api-host>/Prod');
        return base + a.webhookPath;
    }

    // ------------------------------------------------------------ helpers

    isPaid(p: FinancePayment): boolean {
        return ['PAID', 'PARTIALLY_REFUNDED', 'REFUNDED', 'DISPUTED'].includes(p.paymentStatus || '')
            || ['success', 'succeeded'].includes((p.paymentStatus || '').toLowerCase());
    }

    net(p: FinancePayment): number {
        return this.isPaid(p) ? (p.amount || 0) - (p.stripeFee || 0) - (p.amountRefunded || 0) : 0;
    }

    // ------------------------------------------------------------ DataTables

    private rebuildTable(which: 'payments' | 'exceptions' | 'payouts' | 'accounts'): void {
        // Deferred a tick: the cell templates (ViewChild) exist only after the first change detection, and the
        // <table> must be removed before it is re-created with the new options.
        const flag = which + 'DtShow' as 'paymentsDtShow';
        (this as any)[flag] = false;
        setTimeout(() => {
            (this as any)[which + 'Dt'] = this.buildOptions(which);
            (this as any)[flag] = true;
        });
    }

    private buildOptions(which: string): RanaDtOptions {
        const right = 'text-right';
        const dash = (v: string) => v || '—';
        switch (which) {
            case 'payments':
                return ranaDtOptions({
                    data: this._payments,
                    order: [[0, 'desc']],
                    columns: [
                        {title: 'Date', data: null, render: (d, t, p) => t === 'display' || t === 'filter'
                                ? escapeHtml(p.createdEpoch ? new Date(p.createdEpoch).toLocaleString([], {dateStyle: 'medium', timeStyle: 'short'}) : (p.createdDate || '—'))
                                : (p.createdEpoch || 0)},
                        {title: 'Payer', data: null, render: (d, t, p) => escapeHtml(p.customerEmail || p.userId || '—')},
                        {title: 'What', data: null, render: (d, t, p) => escapeHtml(this.purposeLabel(p.purpose) + (p.eventTitle ? ' · ' + p.eventTitle : ''))},
                        {title: 'Amount', data: null, className: right, render: (d, t, p) => t === 'display' ? escapeHtml(this.money(p.amount)) : (p.amount || 0)},
                        {title: 'Stripe fee', data: null, className: right, render: (d, t, p) => this.isPaid(p) ? escapeHtml(this.money(p.stripeFee)) : '—'},
                        {title: 'Net', data: null, className: right, render: (d, t, p) => this.isPaid(p) ? escapeHtml(this.money(this.net(p))) : '—'},
                        {title: 'Refunded', data: null, className: right, render: (d, t, p) => p.amountRefunded ? escapeHtml(this.money(p.amountRefunded)) : '—'},
                        {title: 'Status', data: null, render: (d, t, p) => `<span class="rana-badge ${this.statusClass(p.paymentStatus)}">${escapeHtml(p.paymentStatus || '—')}</span>`},
                        {title: 'Payout', data: null, render: (d, t, p) => escapeHtml(p.payoutStatus
                                ? p.payoutStatus + (p.payoutArrivalDate ? ' · ' + new Date(p.payoutArrivalDate).toLocaleDateString([], {month: 'short', day: 'numeric'}) : '')
                                : '—')}
                    ],
                    rowCallback: (row: any, data: any) => {
                        row.classList.add('fin-row');
                        row.classList.toggle('active', this.selected?.id === data.id);
                        row.onclick = () => {
                            this.select(data);
                            row.parentElement?.querySelectorAll('tr.active').forEach((r: Element) => r.classList.remove('active'));
                            row.classList.add('active');
                        };
                    }
                });
            case 'exceptions':
                return ranaDtOptions({
                    data: this._exceptions,
                    columns: [
                        {title: 'Issue', data: null, render: (d, t, e) => `<span class="rana-badge ${this.exceptionClass(e.severity)}">${escapeHtml(String(e.category || '').replace('_', ' '))}</span>`
                                + `<div class="small text-muted">${escapeHtml(e.title)}</div>`},
                        {title: 'Payer', data: null, render: (d, t, e) => escapeHtml(e.payment.customerEmail || e.payment.userId || '—')},
                        {title: 'What', data: null, render: (d, t, e) => escapeHtml(this.purposeLabel(e.payment.purpose) + (e.payment.eventTitle ? ' · ' + e.payment.eventTitle : ''))},
                        {title: 'Amount', data: null, className: right, render: (d, t, e) => t === 'display' ? escapeHtml(this.money(e.payment.amount)) : (e.payment.amount || 0)},
                        {title: 'Status', data: null, render: (d, t, e) => `<span class="rana-badge ${this.statusClass(e.payment.paymentStatus)}">${escapeHtml(e.payment.paymentStatus)}</span>`},
                        {title: '', data: null, orderable: false, ngTemplateRef: {ref: this.exceptionActionsTpl as TemplateRef<any>, context: {captureEvents: () => {}}}}
                    ]
                });
            case 'payouts':
                return ranaDtOptions({
                    data: this._payouts || [],
                    order: [[0, 'desc']],
                    columns: [
                        {title: 'Arrives', data: null, render: (d, t, p) => t === 'display'
                                ? escapeHtml(p.arrivalDate ? new Date(p.arrivalDate).toLocaleDateString([], {dateStyle: 'medium'}) : '—')
                                : (p.arrivalDate ? new Date(p.arrivalDate).getTime() : 0)},
                        {title: 'Account', data: null, render: (d, t, p) => `<span class="fin-mono">${escapeHtml(p.accountRef)}</span>`},
                        {title: 'Bank', data: null, render: (d, t, p) => escapeHtml(p.bank || p.method || '—')},
                        {title: 'Amount', data: null, className: right, render: (d, t, p) => t === 'display' ? escapeHtml(this.money(p.amount)) : (p.amount || 0)},
                        {title: 'Status', data: null, render: (d, t, p) => {
                                const css = p.status === 'paid' ? 'rana-badge-success'
                                    : (p.status === 'failed' || p.status === 'canceled' ? 'rana-badge-danger' : 'rana-badge-warning');
                                return `<span class="rana-badge ${css}">${escapeHtml(p.status)}</span>`;
                            }},
                        {title: 'Payments', data: null, className: right, render: (d, t, p) => String(p.rangePayments ?? '')},
                        {title: 'Matched to Rana', data: null, className: right, render: (d, t, p) => {
                                const unknown = p.unknownCharges?.length ? ` (${p.unknownCharges.length} unknown)` : '';
                                const text = escapeHtml(String(p.matchedPayments ?? '') + unknown);
                                return p.unknownCharges?.length ? `<span class="text-danger">${text}</span>` : text;
                            }}
                    ]
                });
            default: // accounts
                return ranaDtOptions({
                    data: this._accounts,
                    columns: [
                        {title: 'Account', data: null, render: (d, t, a) => `<span class="fin-mono">${escapeHtml(a.ref)}</span>`},
                        {title: 'Used by', data: null, render: (d, t, a) => escapeHtml((a.usedBy || []).slice(0, 3).join(', ')
                                + ((a.usedBy || []).length > 3 ? ' +' + (a.usedBy.length - 3) + ' more' : ''))},
                        {title: 'Webhook URL', data: null, render: (d, t, a) => `<span class="fin-mono small">${escapeHtml(this.webhookUrl(a))}</span>`},
                        {title: 'Webhook', data: null, render: (d, t, a) =>
                                `<span class="rana-badge ${a.webhookConfigured ? 'rana-badge-success' : 'rana-badge-warning'}">${a.webhookConfigured ? 'Configured' : 'Not set'}</span>`},
                        {title: '', data: null, orderable: false, ngTemplateRef: {ref: this.accountSecretTpl as TemplateRef<any>, context: {captureEvents: () => {}}}}
                    ]
                });
        }
    }

    money(cents: number | null | undefined): string {
        return new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format((cents || 0) / 100);
    }

    dollars(cents: number | null | undefined): string {
        return ((cents || 0) / 100).toFixed(2);
    }

    purposeLabel(purpose?: string): string {
        return this.purposes.find(p => p.value === purpose)?.label || (purpose ? purpose : 'Legacy');
    }

    statusClass(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'PAID': case 'SUCCESS': return 'rana-badge-success';
            case 'PARTIALLY_REFUNDED': case 'REFUNDED': return 'rana-badge-info';
            case 'DISPUTED': case 'FAILED': return 'rana-badge-danger';
            case 'EXPIRED': case 'CANCELLED': return 'rana-badge-neutral';
            default: return 'rana-badge-warning';
        }
    }

    stripeLink(p: FinancePayment): string | null {
        if (!p.paymentIntentId) return null;
        return 'https://dashboard.stripe.com/' + (p.livemode === false ? 'test/' : '') + 'payments/' + p.paymentIntentId;
    }

    exceptionClass(severity: string): string {
        return severity === 'error' ? 'rana-badge-danger' : severity === 'warning' ? 'rana-badge-warning' : 'rana-badge-info';
    }

    private replace(updated: FinancePayment): void {
        this.payments = this.payments.map(p => p.id === updated.id ? updated : p);
        if (this.selected?.id === updated.id) this.selected = updated;
    }

    private fail(message: string, err: any): void {
        console.error(message, err);
        this.toastr.error(err?.error?.error || err?.error?.message || message);
    }
}
